// src/repos/AppRepoProvider.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api as http, extractJsonObject } from "../services/api";

// ✅ endpoints
const API_CUSTOMERS = "getcustomersmd.php";
const API_RATEMD = "get_ratemd.php";

// -------- helpers --------
const lsGet = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const lsSet = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch {}
};
const safeJsonParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};
const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const normStr = (v) => (v == null ? "" : String(v).trim());
const normTypeReq = (t) => {
  const s = normStr(t).toLowerCase();
  if (s === "cow" || s === "c") return "cow";
  if (s === "buf" || s === "buffalo" || s === "b") return "buf";
  return "";
};
const chartKey = (type, no) => `${normTypeReq(type)}|${Number(no) || 0}`;

// supports:
//  - [ {...}, {...} ]
//  - { ok:true, data:[...] }
function extractCustomerArray(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

// get_ratemd.php returns: { ok:true, data: {...} }
// data can be:
//  - grid/rows array
//  - or POINTS object: { points:{ initial, fat_steps, snf_steps } }
function extractPointsArray(data) {
  if (!data) return [];

  const p =
    data.points ||
    data.rows ||
    data.data ||
    data.list ||
    data.rate ||
    data.table ||
    null;

  if (Array.isArray(p)) return p;

  if (typeof p === "string") {
    try {
      const j = JSON.parse(p);
      if (Array.isArray(j)) return j;
      if (j && Array.isArray(j.points)) return j.points;
    } catch {}
  }

  return [];
}

// --- POINTS object fallback ---
function computePointsObjectRate(pointsObj, fat, snf) {
  if (!pointsObj) return null;

  const initial = n(pointsObj.initial);

  const fatSteps = Array.isArray(pointsObj.fat_steps)
    ? [...pointsObj.fat_steps]
    : [];
  const snfSteps = Array.isArray(pointsObj.snf_steps)
    ? [...pointsObj.snf_steps]
    : [];

  fatSteps.sort((a, b) => n(a.step) - n(b.step));
  snfSteps.sort((a, b) => n(a.step) - n(b.step));

  const F = n(fat);
  const S = n(snf);

  const piecewise01 = (steps, value) => {
    if (!steps.length) return 0;
    let adj = 0;

    for (let i = 0; i < steps.length; i++) {
      const start = n(steps[i].step);
      const amt = n(steps[i].amount);
      const end = i + 1 < steps.length ? n(steps[i + 1].step) : Infinity;

      if (value <= start) break;

      const upto = Math.min(value, end);
      const delta = Math.max(0, upto - start);

      // 0.1 increments
      adj += delta * 10 * amt;
      if (value < end) break;
    }

    return adj;
  };

  let out = initial + piecewise01(fatSteps, F) + piecewise01(snfSteps, S);

  const mult =
    n(pointsObj.multiplier) ||
    n(pointsObj.per_point) ||
    n(pointsObj.point_value) ||
    1;

  out = out * mult;
  return Number.isFinite(out) ? out : null;
}

function pickRateFromChart(chartData, { fat, snf, digri }) {
  if (!chartData) return null;

  // POINTS object case
  if (
    String(chartData.mode || "").toUpperCase() === "POINTS" &&
    chartData.points &&
    !Array.isArray(chartData.points)
  ) {
    const r = computePointsObjectRate(chartData.points, fat, snf);
    return r == null ? null : r;
  }

  // grid-like case
  const points = extractPointsArray(chartData);
  if (!points.length) return null;

  const F = n(fat);
  const S = n(snf);
  const D = normStr(digri);

  // exact match
  for (const r of points) {
    const rf = n(r.fat ?? r.fat_value ?? r.fatVal ?? r.f);
    const rs = n(r.snf ?? r.snf_value ?? r.snfVal ?? r.s);
    const rd = normStr(r.digri ?? r.degree ?? r.d ?? "");
    const rate = Number(r.rate ?? r.r ?? r.price ?? r.value);
    if (!Number.isFinite(rate)) continue;

    const okDigri = !rd || !D || rd === D;
    const hasSnf = (r.snf ?? r.snf_value ?? r.snfVal ?? r.s) != null;

    if (hasSnf) {
      if (Math.abs(rf - F) < 1e-9 && Math.abs(rs - S) < 1e-9 && okDigri) {
        return rate;
      }
    } else {
      if (Math.abs(rf - F) < 1e-9 && okDigri) {
        return rate;
      }
    }
  }

  // nearest fallback
  let best = null;
  let bestScore = Infinity;

  for (const r of points) {
    const rf = n(r.fat ?? r.fat_value ?? r.fatVal ?? r.f);
    const rs = n(r.snf ?? r.snf_value ?? r.snfVal ?? r.s);
    const rd = normStr(r.digri ?? r.degree ?? r.d ?? "");
    const rate = Number(r.rate ?? r.r ?? r.price ?? r.value);
    if (!Number.isFinite(rate)) continue;

    const hasSnf = (r.snf ?? r.snf_value ?? r.snfVal ?? r.s) != null;
    const digriPenalty = rd && D && rd !== D ? 50 : 0;

    const score = hasSnf
      ? Math.abs(rf - F) + Math.abs(rs - S) + digriPenalty
      : Math.abs(rf - F) + digriPenalty;

    if (score < bestScore) {
      bestScore = score;
      best = rate;
    }
  }

  return best == null ? null : best;
}

function parseApiBody(respData) {
  if (typeof respData === "string") {
    const s = extractJsonObject(respData);
    return safeJsonParse(s) || null;
  }
  return respData || null;
}

// -------- context --------
const RepoContext = createContext(null);

export function AppRepoProvider({ mainuser, children }) {
  const [customersReady, setCustomersReady] = useState(false);
  const [ratesReady, setRatesReady] = useState(false);
  const [version, setVersion] = useState(0);

  // store FULL customer objects by kno
  const customerByKnoRef = useRef(new Map());

  // store charts by key: `${type}|${ratechartno}`
  const rateChartMapRef = useRef(new Map());

  const keys = useMemo(() => {
    const u = normStr(mainuser);
    return {
      custJson: `customers_json|${u}`,
      custEtag: `customers_etag|${u}`,
      rateChartJson: (type, no) => `ratemd_json|${u}|${type}|${no}`,
    };
  }, [mainuser]);

  useEffect(() => {
    const u = normStr(mainuser);
    if (!u) return;

    // ---------------- customers cache warmup ----------------
    setCustomersReady(false);
    customerByKnoRef.current = new Map();

    const cachedCust = lsGet(keys.custJson);
    const cachedObj = cachedCust ? safeJsonParse(cachedCust) : null;
    const cachedArr = extractCustomerArray(cachedObj);

    if (cachedArr.length) {
      const map = new Map();
      for (const c of cachedArr) {
        const kno = normStr(c.kno ?? c.sr ?? c.no ?? "");
        if (kno) map.set(kno, c);
      }
      customerByKnoRef.current = map;
      setCustomersReady(true);
      setVersion((v) => v + 1);
    }

    // ---------------- rates cache warmup ----------------
    setRatesReady(false);
    rateChartMapRef.current = new Map();

    for (const type of ["cow", "buf"]) {
      for (const no of [1, 2]) {
        const cached = lsGet(keys.rateChartJson(type, no));
        const obj = cached ? safeJsonParse(cached) : null;
        if (obj && obj.ok) {
          rateChartMapRef.current.set(chartKey(type, no), obj.data ?? null);
        }
      }
    }

    if (rateChartMapRef.current.size > 0) {
      setRatesReady(true);
      setVersion((v) => v + 1);
    }

    // ---------------- network refresh ----------------
    let cancelled = false;

    async function refreshCustomers() {
      const etag = lsGet(keys.custEtag) || "";
      try {
        const resp = await http.get(API_CUSTOMERS, {
          params: { mainuser: u, nocache: Date.now() },
          headers: etag ? { "If-None-Match": etag } : undefined,
          validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        });

        if (cancelled) return;

        if (resp.status === 304) {
          setCustomersReady(true);
          return;
        }

        const body = parseApiBody(resp.data);
        const arr = extractCustomerArray(body);

        const map = new Map();
        for (const c of arr) {
          const kno = normStr(c.kno ?? c.sr ?? c.no ?? "");
          if (kno) map.set(kno, c);
        }
        customerByKnoRef.current = map;

        lsSet(keys.custJson, JSON.stringify(arr));
        const newEtag = resp.headers?.etag;
        if (newEtag) lsSet(keys.custEtag, newEtag);

        setCustomersReady(true);
        setVersion((v) => v + 1);
      } catch {
        setCustomersReady(true);
      }
    }

    async function refreshRateChart(type, ratechartno) {
      try {
        const resp = await http.get(API_RATEMD, {
          params: {
            mainuser: u,
            type,
            ratechartno,
            ts: Date.now(),
          },
          validateStatus: (s) => s >= 200 && s < 300,
        });

        if (cancelled) return;

        const body = parseApiBody(resp.data);

        if (body && body.ok) {
          const data = body.data ?? null;
          rateChartMapRef.current.set(chartKey(type, ratechartno), data);
          lsSet(
            keys.rateChartJson(type, ratechartno),
            JSON.stringify({ ok: true, data })
          );
          setVersion((v) => v + 1);
        }
      } catch {
        // ignore (keep cached)
      }
    }

    async function refreshRatesAll() {
      await Promise.all([
        refreshRateChart("cow", 1),
        refreshRateChart("cow", 2),
        refreshRateChart("buf", 1),
        refreshRateChart("buf", 2),
      ]);
      if (!cancelled) setRatesReady(true);
    }

    refreshCustomers();
    refreshRatesAll();

    return () => {
      cancelled = true;
    };
  }, [mainuser, keys]);

  const repo = useMemo(() => {
    const u = normStr(mainuser);

    // ---- rates helpers INSIDE memo (so no-undef) ----
    const getRateChart = (type, no = 1) => {
      const t = normTypeReq(type);
      const nno = Number(no) === 2 ? 2 : 1;
      if (!t) return null;
      return rateChartMapRef.current.get(chartKey(t, nno)) || null;
    };

    const refreshRateChart = async (type, no = 1) => {
      const t = normTypeReq(type);
      const nno = Number(no) === 2 ? 2 : 1;
      if (!u || !t) return null;

      const resp = await http.get(API_RATEMD, {
        params: { mainuser: u, type: t, ratechartno: nno, ts: Date.now() },
      });

      const body = parseApiBody(resp.data);
      if (body && body.ok) {
        const data = body.data ?? null;
        rateChartMapRef.current.set(chartKey(t, nno), data);
        lsSet(keys.rateChartJson(t, nno), JSON.stringify({ ok: true, data }));
        setRatesReady(true);
        setVersion((v) => v + 1);
        return data;
      }
      return null;
    };

    const listRateChartNos = (type) => {
      const t = normTypeReq(type);
      if (!t) return [];
      const out = new Set();
      for (const k of rateChartMapRef.current.keys()) {
        const [kt, kn] = String(k).split("|");
        if (kt === t) out.add(Number(kn) || 0);
      }
      return Array.from(out).sort((a, b) => a - b);
    };

    return {
      customers: {
        ready: customersReady,
        size: () => customerByKnoRef.current.size,

        getCustomerByKno: (kno) =>
          customerByKnoRef.current.get(normStr(kno)) || null,

        getNameByKno: (kno) => {
          const c = customerByKnoRef.current.get(normStr(kno));
          return normStr(c?.cname ?? c?.name ?? "");
        },

        // ✅ for Farmers page
        getAll: () => Array.from(customerByKnoRef.current.values()),
        list: () => Array.from(customerByKnoRef.current.values()),
        fetchAll: () => Array.from(customerByKnoRef.current.values()),
        fetchCustomers: () => Array.from(customerByKnoRef.current.values()),

        // ✅ chart no from customer record
        getChartNoFor: (kno, type) => {
          const c = customerByKnoRef.current.get(normStr(kno));
          const t = normTypeReq(type);
          if (!c || !t) return 1;
          const v = t === "cow" ? n(c.cow_ratechartno) : n(c.buff_ratechartno);
          return v === 2 ? 2 : 1;
        },
      },

      rates: {
        ready: ratesReady,

        // ✅ get computed rate using cached chart
        getRate: ({ type, fat, snf, digri, ratechartno = 1 }) => {
          const t = normTypeReq(type);
          const no = Number(ratechartno) === 2 ? 2 : 1;
          if (!t) return null;
          const chart = getRateChart(t, no);
          return pickRateFromChart(chart, { fat, snf, digri });
        },

        // ✅ give raw chart data (Ratechart page can use)
        getRateChart,

        // ✅ manually refresh chart
        refreshRateChart,

        // ✅ list loaded chart nos
        listRateChartNos,
      },

      _version: version,
    };
  }, [customersReady, ratesReady, version, mainuser, keys]);

  return <RepoContext.Provider value={repo}>{children}</RepoContext.Provider>;
}

export function useCustomerRepo() {
  const ctx = useContext(RepoContext);
  if (!ctx)
    throw new Error("useCustomerRepo must be used inside AppRepoProvider");
  return ctx.customers;
}

export function useRateRepo() {
  const ctx = useContext(RepoContext);
  if (!ctx) throw new Error("useRateRepo must be used inside AppRepoProvider");
  return ctx.rates;
}
